import { RefObject, useEffect, useRef } from 'react'

export function isContainedWithinNode(element: Node, node: Node): boolean {
  if (element === node) return true
  const contains = (node as any).contains as ((n: Node) => boolean) | undefined
  return typeof contains === 'function' ? contains.call(node, element) : false
}

export function useOnClickedOutside<E extends Node>(
  onClicked: ((event: MouseEvent) => void) | false,
  excludedElements?: (Node | null)[]
): RefObject<E | null> {
  const ref = useRef<E | null>(null)

  const onClickedRef = useRef<((event: MouseEvent) => void) | false>(onClicked)
  const excludedRef = useRef<(Node | null)[] | undefined>(excludedElements)

  useEffect(() => {
    onClickedRef.current = onClicked
    excludedRef.current = excludedElements
  }, [onClicked, excludedElements])

  useEffect(() => {
    if (!onClicked) return

    const onClickedOutside = (event: MouseEvent) => {
      const cb = onClickedRef.current
      if (!cb) return

      const target = event.target as Node | null
      if (!target) return

      const current = ref.current
      if (!current) return

      const path =
        typeof (event as any).composedPath === 'function'
          ? ((event as any).composedPath() as Node[])
          : null

      const excluded = excludedRef.current
      const isExcluded = excluded?.some((excludedElement) => {
        if (!excludedElement) return false
        return path
          ? path.includes(excludedElement)
          : isContainedWithinNode(target, excludedElement)
      })

      const isInside = path ? path.includes(current) : isContainedWithinNode(target, current)

      if (!isInside && !isExcluded) cb(event)
    }

    const timeoutId = window.setTimeout(() => {
      document.documentElement.addEventListener('click', onClickedOutside, true)
    }, 0)

    return () => {
      window.clearTimeout(timeoutId)
      document.documentElement.removeEventListener('click', onClickedOutside, true)
    }
  }, [onClicked])

  return ref
}
